import axios from "@/lib/axios";

import { useMutation, useQuery } from "@tanstack/vue-query";

import useCustomMutation from "@/composables/useCustomMutation.ts";
import { MutationParams } from "@/interfaces/mutationParams.ts";
import useCompanyDeparments from "@/modules/companies/composables/useCompanyDeparments.ts";
import { T } from "@vueuse/motion/dist/nuxt-b4cb9b59";
import { useToast } from "vue-toastification";
import { NewDataUser, PendingRequest } from "../interfaces/pendingRequest";

interface Params extends Omit<Partial<PendingRequest<T>>, "id"> {
  id?: string | number;
  includes?: string[];
  type?: string;
  // Motivo del rechazo: se persiste en new_data.comment del approval.
  comment?: string;
}

interface Props {
  params: Params;
  immediate?: boolean;
}

const getApproval = async <T>(params?: Params) => {
  if (!params) return;
  const { data } = await axios.get<PendingRequest<T>>(
    `/api/approvals/${params.id}`,
  );
  return data;
};

const aprovedApproval = async <T>(params: Params) => {
  const { data } = await axios.post<PendingRequest<T>>(
    `/api/approvals/${params.id}/approve`,
  );

  return data;
};

const rejectApproval = async <T>(params: Params) => {
  const { data } = await axios.post<PendingRequest<T>>(
    `/api/approvals/${params.id}/reject`,
    { comment: params.comment },
  );

  return data;
};

const usePendingRequest = <T>(props?: Props) => {
  const { params, immediate = true } = props || {};

  const toast = useToast();

  const {
    data: approvalRequests,
    isLoading: approvalIsLoading,
    refetch: approvalRefetch,
    isFetching: approvalIsFetching,
  } = useQuery(
    ["ApprovalRequest", params?.id],
    () => getApproval<T>({ ...params }),
    {
      enabled: params ? immediate : false,
      retry: false,
    },
  );

  const approveMutation = useMutation(
    ({ params }: MutationParams<Params>) => aprovedApproval(params),
    useCustomMutation(toast, "create"),
  );

  const rejectedMutation = useMutation(
    ({ params }: MutationParams<Params>) => rejectApproval(params),
    useCustomMutation(toast, "create"),
  );

  return {
    approvalRequests,
    approvalIsLoading,
    approvalIsFetching,
    approvalRefetch,

    approveMutation: approveMutation,
    approveMutationAsync: approveMutation.mutateAsync,

    rejectedMutation: rejectedMutation,
    rejectedMutationAsync: rejectedMutation.mutateAsync,
  };
};

export default usePendingRequest;
